import { Injectable } from '@angular/core';

/**
 * Non-master-form Enter navigation (e.g. simple screens without appMasterFormKeyNav).
 * Master forms must not use this — they use MasterFormKeyNavDirective only.
 */
@Injectable({ providedIn: 'root' })
export class KeyboardNavigationService {
  private excludedSelectors = [
    '.sales-items-grid',
    'app-party-grid',
    'textarea',
    '[contenteditable="true"]',
  ];

  handleGlobalEnterKey(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement;

    if (target.closest('[appMasterFormKeyNav]')) {
      return false;
    }

    if (!['INPUT', 'SELECT'].includes(target.tagName)) {
      return false;
    }

    if (target.tagName === 'TEXTAREA') {
      return false;
    }

    if (this.isInExcludedContainer(target)) {
      return false;
    }

    const isAutocompleteOpen = this.isAutocompleteOpenForElement(target);
    const isSelectOpen = this.isSelectOpenForElement(target);

    if (isAutocompleteOpen || isSelectOpen) {
      if (isAutocompleteOpen) {
        this.closeAutocompletePanelForElement(target);
      }
      if (isSelectOpen) {
        this.closeSelectPanelForElement(target);
      }
      setTimeout(() => {
        this.focusNextField(target);
      }, 0);
      event.preventDefault();
      return true;
    }

    if (this.hasCustomHandler(target)) {
      return false;
    }

    event.preventDefault();
    this.focusNextField(target);
    return true;
  }

  private hasCustomHandler(element: HTMLElement): boolean {
    const hasKeydown =
      element.hasAttribute('(keydown)') ||
      element.hasAttribute('(keydown.enter)') ||
      element.getAttribute('ng-reflect-keydown') !== null;

    if (element.hasAttribute('data-skip-enter-navigation')) {
      return true;
    }

    const form = element.closest('form');
    if (form?.hasAttribute('data-skip-enter-navigation')) {
      return true;
    }

    return hasKeydown;
  }

  private isInExcludedContainer(element: HTMLElement): boolean {
    return this.excludedSelectors.some((selector) => {
      return element.closest(selector) !== null;
    });
  }

  /** Uses only the focused control + its aria-controls/aria-owns panel — no document-wide scan. */
  private isAutocompleteOpenForElement(element: HTMLElement): boolean {
    const inputElement = (element.closest('input[matAutocomplete]') ||
      (element.tagName === 'INPUT' ? element : null)) as HTMLInputElement | null;
    if (!inputElement) {
      return false;
    }
    if (inputElement.getAttribute('aria-expanded') !== 'true') {
      return false;
    }
    const panelId = inputElement.getAttribute('aria-controls') || inputElement.getAttribute('aria-owns');
    if (!panelId) {
      return false;
    }
    const panel = document.getElementById(panelId);
    if (!panel?.classList.contains('mat-mdc-autocomplete-panel') && !panel?.classList.contains('mat-autocomplete-panel')) {
      return false;
    }
    const style = window.getComputedStyle(panel);
    return style.display !== 'none' && style.visibility !== 'hidden' && (panel as HTMLElement).getClientRects().length > 0;
  }

  private closeAutocompletePanelForElement(element: HTMLElement): void {
    const inputElement = (element.closest('input') || element) as HTMLInputElement;
    if (inputElement instanceof HTMLInputElement) {
      inputElement.dispatchEvent(
        new KeyboardEvent('keydown', {
          key: 'Escape',
          keyCode: 27,
          bubbles: false,
          cancelable: true,
        }),
      );
    }
  }

  private isSelectOpenForElement(element: HTMLElement): boolean {
    const active = (element.closest('mat-select') || element) as HTMLElement | null;
    if (!active || active.tagName !== 'MAT-SELECT') {
      return false;
    }
    if (active.getAttribute('aria-expanded') !== 'true') {
      return false;
    }
    const panelId = active.getAttribute('aria-controls') || active.getAttribute('aria-owns');
    if (!panelId) {
      return false;
    }
    const panel = document.getElementById(panelId);
    if (!panel?.classList.contains('mat-mdc-select-panel') && !panel?.classList.contains('mat-select-panel')) {
      return false;
    }
    const style = window.getComputedStyle(panel);
    return style.display !== 'none' && style.visibility !== 'hidden' && panel.getClientRects().length > 0;
  }

  private closeSelectPanelForElement(element: HTMLElement): void {
    const selectHost = element.closest('mat-select') as HTMLElement | null;
    if (selectHost) {
      selectHost.dispatchEvent(
        new KeyboardEvent('keydown', {
          key: 'Escape',
          keyCode: 27,
          bubbles: false,
          cancelable: true,
        }),
      );
    }
  }

  private focusNextField(currentElement: HTMLElement): void {
    const form =
      currentElement.closest('form') ||
      currentElement.closest('[class*="form"]') ||
      document.body;

    const focusableSelector = `
      input:not([disabled]):not([readonly]):not([tabindex="-1"]):not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="reset"]),
      select:not([disabled]):not([readonly]):not([tabindex="-1"])
    `;

    const focusableElements = Array.from(form.querySelectorAll(focusableSelector)) as HTMLElement[];

    const validElements = focusableElements.filter(
      (el) => !this.isInExcludedContainer(el) && !this.hasCustomHandler(el),
    );

    const currentIndex = validElements.indexOf(currentElement as HTMLElement);

    if (currentIndex !== -1 && currentIndex < validElements.length - 1) {
      const nextElement = validElements[currentIndex + 1];
      nextElement.focus();
      if (nextElement instanceof HTMLInputElement) {
        nextElement.select();
      }
    }
  }
}
